import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { apiFetch, clearAuthToken } from '../api/client';
import OpsLayout from '../components/Layout/OpsLayout';
import './OpsNfps.css';

const STATUS_CAUSE_CODES = [
  'nonpayment_or_funding_failure',
  'compliance_hold',
  'fraud_or_risk_hold',
  'owner_requested_pause',
  'onboarding_incomplete',
  'data_quality_or_duplicate',
  'system_admin_action',
  'other',
];

const STORE_EXCLUSION_REASON_CODES = [
  'temporary_closure',
  'renovation',
  'seasonal',
  'ownership_change',
  'staffing',
  'not_yet_open',
  'permit_license_pending',
  'relocation_in_progress',
  'duplicate_listing',
  'not_my_store',
  'business_requested_pause',
  'system_admin_action',
  'other',
];

const MANUAL_OVERRIDE_REASON_CODES = [
  'compliance_hold',
  'fraud_or_risk_hold',
  'data_quality_or_duplicate',
  'system_admin_action',
  'other',
];

const TIP_POLICY_REASON_CODES = [
  'google_places_signal',
  'manual_ops_review',
  'merchant_attestation',
  'data_quality_issue',
  'override_for_beta',
  'other',
];

const ACTION_CONFIG = {
  accountStatus: {
    label: 'Update Account Status',
    endpointKey: 'accountStatus',
    options: ['active', 'suspended', 'inactive'],
    reasonCodes: STATUS_CAUSE_CODES,
  },
  brandStatus: {
    label: 'Update EIN Status',
    endpointKey: 'brandStatus',
    options: ['active', 'inactive'],
    reasonCodes: STATUS_CAUSE_CODES,
  },
  lifecycleStatus: {
    label: 'Update EIN Operational Stage',
    endpointKey: 'lifecycleStatus',
    options: ['prospect', 'onboarding', 'pending_verification', 'active', 'suspended', 'closed'],
    reasonCodes: STATUS_CAUSE_CODES,
  },
  storeEnrollment: {
    label: 'Update Store Coverage',
    endpointKey: 'storeEnrollment',
    options: ['active', 'paused'],
    reasonCodes: STORE_EXCLUSION_REASON_CODES,
  },
  hiddenFromConsumer: {
    label: 'Update Consumer Discovery',
    endpointKey: 'hiddenFromConsumer',
    options: ['hide', 'unhide'],
    reasonCodes: MANUAL_OVERRIDE_REASON_CODES,
  },
  tipPolicy: {
    label: 'Update Tip Classification',
    endpointKey: 'tipPolicy',
    options: ['fine_dining', 'casual', 'fast_casual', 'clear'],
    reasonCodes: TIP_POLICY_REASON_CODES,
  },
  addNote: {
    label: 'Add Audit Note',
    endpointKey: 'addNote',
    method: 'POST',
    options: [],
    reasonCodes: [],
  },
};

const FUNDING_POLICY_FIELDS = [
  { key: 'initialTopUpCents', label: 'Initial Top-Up' },
  { key: 'autoThresholdCents', label: 'Auto Trigger' },
  { key: 'manualThresholdCents', label: 'Manual Trigger' },
  { key: 'reloadCents', label: 'Top-Up Amount' },
];

function initialActionState() {
  return {
    value: '',
    reasonCode: '',
    reasonNote: '',
    overrideTransition: false,
    storeId: '',
    placeId: '',
    accountId: '',
    effectiveFrom: '',
    effectiveTo: '',
    enrollmentHiddenFromConsumer: false,
    loading: false,
    error: '',
    success: '',
  };
}

function emptyFundingOverrideDraft() {
  return {
    active: false,
    initialTopUpCents: '',
    autoThresholdCents: '',
    manualThresholdCents: '',
    reloadCents: '',
    notes: '',
  };
}

function centsToInput(cents) {
  if (cents === null || cents === undefined || cents === '') return '';
  const n = Number(cents);
  if (!Number.isFinite(n)) return '';
  return String(n / 100);
}

function overrideToDraft(override) {
  return {
    active: override?.active === true,
    initialTopUpCents: centsToInput(override?.initialTopUpCents),
    autoThresholdCents: centsToInput(override?.autoThresholdCents),
    manualThresholdCents: centsToInput(override?.manualThresholdCents),
    reloadCents: centsToInput(override?.reloadCents),
    notes: String(override?.notes || ''),
  };
}

function moneyInputToCents(value, fieldLabel) {
  const raw = String(value ?? '').trim();
  if (!raw) return undefined;
  const normalized = raw.replace(/[$,]/g, '');
  const n = Number(normalized);
  if (!Number.isFinite(n) || n < 0) {
    throw new Error(`${fieldLabel} must be a non-negative dollar amount`);
  }
  return Math.round(n * 100);
}

function draftToOverridePayload(draft) {
  const payload = {
    active: draft?.active === true,
    notes: String(draft?.notes || '').trim(),
  };
  FUNDING_POLICY_FIELDS.forEach((field) => {
    const cents = moneyInputToCents(draft?.[field.key], field.label);
    if (cents !== undefined) payload[field.key] = cents;
  });
  return payload;
}

function hasActiveFundingOverride(override) {
  return override?.active === true;
}

function getBusinessFundingOverride(entry) {
  return entry?.fundingPolicy?.overrides?.brand || entry?.fundingPolicyOverride || null;
}

function getStoreFundingOverride(store) {
  return store?.fundingPolicyOverride || null;
}

function collectFundingSourceKeys(...groups) {
  return Array.from(new Set(
    groups
      .flatMap((group) => Object.values(group || {}))
      .map((source) => String(source?.key || '').toLowerCase())
      .filter(Boolean)
  ));
}

function buildFundingOverrideIndicator(perStoreSources = {}, aggregateSources = {}) {
  const keys = collectFundingSourceKeys(perStoreSources, aggregateSources);
  if (keys.includes('mixed')) {
    return { value: 'Mixed', tone: 'info', title: 'Multiple override sources affect the current funding policy.' };
  }

  const labels = [];
  if (keys.includes('store_override')) labels.push('Store');
  if (keys.includes('manual_override')) labels.push('EIN');
  if (keys.includes('account_override')) labels.push('Account');

  if (!labels.length) {
    return { value: 'None', tone: 'good', title: 'Current funding policy is using policy defaults.' };
  }

  return {
    value: labels.join(' + '),
    tone: 'info',
    title: `${labels.join(' + ')} override is affecting the current funding policy.`,
  };
}

function compactParts(parts = [], separator = ', ') {
  return parts
    .map((part) => String(part || '').trim())
    .filter(Boolean)
    .join(separator);
}

function parseStoreLocation(store) {
  const full = String(store?.location || store?.formattedAddress || store?.addressText || store?.address || '').trim();
  const parts = full.split(',').map((part) => part.trim()).filter(Boolean);
  return {
    street: parts[0] || full,
    city: parts[1] || '',
    stateZip: parts[2] || '',
  };
}

function formatStoreAddress(store) {
  if (!store) return '';
  const parsed = parseStoreLocation(store);
  return String(store?.address || '').trim() || parsed.street || '';
}

function formatStoreCityState(store) {
  const parsed = parseStoreLocation(store);
  const city = store?.city || store?.locality || parsed.city || '';
  const stateZip = compactParts([store?.state, store?.zipCode], ' ') || parsed.stateZip || '';
  return compactParts([city, stateZip]);
}

function formatStoreSelectorLabel(store) {
  const name = store?.name || 'Store';
  const location = compactParts([formatStoreAddress(store), formatStoreCityState(store)]);
  return location ? `${name} - ${location}` : name;
}

function Field({ label, value, style }) {
  return (
    <div className="ops-kv" style={style}>
      <div className="k">{label}</div>
      <div className="v">{value ?? '—'}</div>
    </div>
  );
}

function SourceTag({ source }) {
  const key = String(source?.key || '').toLowerCase();
  const label = key === 'manual_override'
    ? 'EIN Override'
    : (source?.label || titleCaseWords(key || 'policy default'));
  const palette = key === 'manual_override'
    ? { background: '#fef3c7', color: '#92400e', border: '#fde68a' }
    : key === 'store_override'
      ? { background: '#ede9fe', color: '#5b21b6', border: '#ddd6fe' }
    : key === 'account_override'
      ? { background: '#dbeafe', color: '#1e40af', border: '#bfdbfe' }
      : key === 'mixed'
        ? { background: '#f1f5f9', color: '#334155', border: '#cbd5e1' }
      : { background: '#ecfdf5', color: '#047857', border: '#bbf7d0' };

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        borderRadius: 999,
        padding: '2px 8px',
        fontSize: 12,
        fontWeight: 700,
        background: palette.background,
        color: palette.color,
        border: `1px solid ${palette.border}`,
      }}
    >
      {label}
    </span>
  );
}

function FundingPolicyMetric({ label, cents, source }) {
  return (
    <div
      style={{
        border: '1px solid #e2e8f0',
        borderRadius: 12,
        padding: '10px 12px',
        minWidth: 0,
        background: '#f8fafc',
      }}
    >
      <div style={{ color: '#64748b', fontSize: 12, fontWeight: 800, marginBottom: 5 }}>
        {label}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', minWidth: 0 }}>
        <span style={{ color: '#0f172a', fontSize: 18, fontWeight: 850 }}>{formatMoney(cents)}</span>
        <SourceTag source={source} />
      </div>
    </div>
  );
}

function PolicyMetric({ label, value, valueFontSize = 18 }) {
  return (
    <div
      style={{
        border: '1px solid #e2e8f0',
        borderRadius: 12,
        padding: '10px 12px',
        minWidth: 0,
        background: '#f8fafc',
      }}
    >
      <div style={{ color: '#64748b', fontSize: 12, fontWeight: 800, marginBottom: 5 }}>
        {label}
      </div>
      <div style={{ color: '#0f172a', fontSize: valueFontSize, fontWeight: 850, overflowWrap: 'anywhere' }}>
        {value ?? '—'}
      </div>
    </div>
  );
}

function formatMoney(cents) {
  const n = Number(cents || 0);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(n / 100);
}

function formatPercent(value) {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return `${n.toFixed(1)}%`;
}

function formatDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
}

function titleCaseWords(value) {
  return String(value || '')
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function formatBlockerLabel(blocker = {}) {
  const code = titleCaseWords(blocker?.code || 'unknown');
  const severity = blocker?.severity ? ` (${blocker.severity})` : '';
  const cause = blocker?.causeCode ? ` - ${titleCaseWords(blocker.causeCode)}` : '';
  return `${code}${severity}${cause}`;
}

function dedupeBlockers(blockers = []) {
  const seen = new Set();
  const out = [];
  (Array.isArray(blockers) ? blockers : []).forEach((blocker) => {
    const key = [
      blocker?.scope || '',
      blocker?.code || '',
      blocker?.entityId || '',
      blocker?.causeCode || '',
    ].join(':');
    if (seen.has(key)) return;
    seen.add(key);
    out.push(blocker);
  });
  return out;
}

function collectRecommendedActions(blockers = []) {
  const seen = new Set();
  const actions = [];
  (Array.isArray(blockers) ? blockers : []).forEach((blocker) => {
    const descriptors = Array.isArray(blocker?.actions) && blocker.actions.length
      ? blocker.actions
      : (Array.isArray(blocker?.recommendedActions) ? blocker.recommendedActions.map((key) => ({ key, label: titleCaseWords(key || ''), opsCanExecute: false })) : []);
    descriptors.forEach((action) => {
      const key = String(action?.key || '');
      if (!key || seen.has(key)) return;
      seen.add(key);
      actions.push({
        key,
        label: action?.label || titleCaseWords(key),
        opsCanExecute: typeof action?.opsCanExecute === 'boolean' ? action.opsCanExecute : false,
      });
    });
  });
  return actions;
}

function formatActionLabel(action) {
  if (action && typeof action === 'object') return action.label || titleCaseWords(action.key || '');
  const label = titleCaseWords(action || '');
  return label === 'Ops' ? 'Ops' : label;
}

function HealthChip({ label, value, tone = '', onEdit, title }) {
  return (
    <button
      type="button"
      className={`ops-chip ${tone}`}
      onClick={onEdit || undefined}
      disabled={!onEdit}
      title={title || ''}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        opacity: onEdit ? 1 : 0.95,
        cursor: onEdit ? 'pointer' : 'default',
        border: 'none',
      }}
    >
      <span>{label}: {value}</span>
      {onEdit ? <span aria-hidden="true">✎</span> : null}
    </button>
  );
}

function SummaryCard({ title, children, footer, actions }) {
  return (
    <article className="ops-nfp-panel" style={{ marginTop: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <h3 className="ops-section-title" style={{ margin: 0 }}>{title}</h3>
        {actions ? <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>{actions}</div> : null}
      </div>
      {children}
      {footer ? <div style={{ marginTop: 12 }}>{footer}</div> : null}
    </article>
  );
}

function ModalSection({ title, description, children, meta }) {
  return (
    <section
      style={{
        border: '1px solid #e2e8f0',
        borderRadius: 14,
        padding: 14,
        display: 'grid',
        gap: 12,
        background: '#ffffff',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontWeight: 850, color: '#0f172a', fontSize: 15 }}>{title}</div>
          {description ? <div style={{ color: '#64748b', fontSize: 13, marginTop: 3 }}>{description}</div> : null}
        </div>
        {meta ? <span className="ops-chip" style={{ fontSize: 12 }}>{meta}</span> : null}
      </div>
      {children}
    </section>
  );
}

function FundingOverrideForm({
  title,
  description,
  draft,
  onChange,
  onSave,
  saving,
  error,
  disabled,
}) {
  const currentDraft = draft || emptyFundingOverrideDraft();
  return (
    <div
      style={{
        border: '1px solid #e2e8f0',
        borderRadius: 14,
        padding: 12,
        display: 'grid',
        gap: 10,
      }}
    >
      <div>
        <div style={{ fontWeight: 800, color: '#0f172a' }}>{title}</div>
        <div style={{ fontSize: 13, color: '#64748b' }}>{description}</div>
      </div>
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 700 }}>
        <input
          type="checkbox"
          checked={currentDraft.active === true}
          onChange={(e) => onChange({ active: e.target.checked })}
          disabled={disabled || saving}
        />
        Override enabled
      </label>
      <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
        {FUNDING_POLICY_FIELDS.map((field) => (
          <label key={field.key} className="ops-nfp-field" style={{ margin: 0 }}>
            <span>{field.label}</span>
            <input
              type="number"
              min="0"
              step="0.01"
              placeholder="inherit"
              value={currentDraft[field.key]}
              onChange={(e) => onChange({ [field.key]: e.target.value })}
              disabled={disabled || saving}
            />
          </label>
        ))}
      </div>
      <label className="ops-nfp-field" style={{ margin: 0 }}>
        <span>Notes</span>
        <textarea
          rows={2}
          value={currentDraft.notes}
          onChange={(e) => onChange({ notes: e.target.value })}
          disabled={disabled || saving}
          placeholder="Reason for override"
        />
      </label>
      <div style={{ color: '#64748b', fontSize: 12 }}>
        Blank values inherit from the next lower scope. Store overrides win over EIN and account overrides.
      </div>
      {error ? <div className="ops-error">{error}</div> : null}
      <div>
        <button type="button" className="ops-btn primary" onClick={onSave} disabled={disabled || saving}>
          {saving ? 'Saving...' : 'Save Override'}
        </button>
      </div>
    </div>
  );
}

function Modal({ open, title, children, onClose, width = 'min(720px, 100%)' }) {
  if (!open) return null;
  return (
    <div
      role="presentation"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
        zIndex: 1000,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
        style={{
          width,
          maxHeight: '90vh',
          overflow: 'auto',
          background: '#ffffff',
          borderRadius: 18,
          padding: 20,
          boxShadow: '0 20px 60px rgba(15, 23, 42, 0.2)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <h3 className="ops-section-title" style={{ margin: 0 }}>{title}</h3>
          <button type="button" className="ops-btn" onClick={onClose}>Close</button>
        </div>
        <div style={{ marginTop: 16 }}>{children}</div>
      </div>
    </div>
  );
}

export default function OpsBusinessDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [business, setBusiness] = useState(null);
  const [accountBusinesses, setAccountBusinesses] = useState([]);
  const [selectedBusinessId, setSelectedBusinessId] = useState('');
  const [expandedGroups, setExpandedGroups] = useState({});
  const [showContact, setShowContact] = useState(false);
  const [showWalletDiagnostics, setShowWalletDiagnostics] = useState(false);
  const [showFundingPolicyModal, setShowFundingPolicyModal] = useState(false);
  const [showVisibilityDiagnostics, setShowVisibilityDiagnostics] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [showBrandingModal, setShowBrandingModal] = useState(false);
  const [clearinghouseDetail, setClearinghouseDetail] = useState(null);
  const [clearinghouseLoading, setClearinghouseLoading] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [modalActionKey, setModalActionKey] = useState('');
  const [consumedAutoAction, setConsumedAutoAction] = useState('');
  const [selectedFundingStoreId, setSelectedFundingStoreId] = useState('');
  const [highlightedGeographyStoreId, setHighlightedGeographyStoreId] = useState('');
  const storeGeographySectionRef = useRef(null);
  const [actions, setActions] = useState({
    accountStatus: initialActionState(),
    brandStatus: initialActionState(),
    lifecycleStatus: initialActionState(),
    storeEnrollment: initialActionState(),
    hiddenFromConsumer: initialActionState(),
    tipPolicy: initialActionState(),
    addNote: initialActionState(),
  });
  const [fundingOverrideDrafts, setFundingOverrideDrafts] = useState({
    account: emptyFundingOverrideDraft(),
    brand: emptyFundingOverrideDraft(),
    store: emptyFundingOverrideDraft(),
  });
  const [fundingOverrideSaving, setFundingOverrideSaving] = useState('');
  const [fundingOverrideErrors, setFundingOverrideErrors] = useState({ account: '', brand: '', store: '' });

  function updateActionState(actionKey, patch) {
    setActions((prev) => ({
      ...prev,
      [actionKey]: { ...prev[actionKey], ...patch },
    }));
  }

  async function load() {
    setLoading(true);
    setError('');
    try {
      const data = await apiFetch(`/ops/businesses/${encodeURIComponent(id)}`);
      const primary = data?.business || null;
      setBusiness(primary);
      setSelectedBusinessId(primary?.id || '');

      let relatedBusinesses = primary ? [primary] : [];
      const accountId = primary?.accountWallet?.accountId || '';
      if (primary && accountId) {
        try {
          const list = await apiFetch('/ops/businesses?limit=500&page=1');
          const siblingIds = Array.from(
            new Set(
              (Array.isArray(list?.businesses) ? list.businesses : [])
                .filter((row) => String(row?.accountId || '') === String(accountId))
                .map((row) => row?.id)
                .filter(Boolean)
            )
          );
          if (!siblingIds.includes(primary.id)) siblingIds.unshift(primary.id);
          const siblingDetails = await Promise.all(
            siblingIds.map(async (businessId) => {
              if (String(businessId) === String(primary.id)) return primary;
              try {
                const resp = await apiFetch(`/ops/businesses/${encodeURIComponent(businessId)}`);
                return resp?.business || null;
              } catch (_err) {
                return null;
              }
            })
          );
          relatedBusinesses = siblingDetails.filter(Boolean);
        } catch (_err) {
          relatedBusinesses = [primary];
        }
      }

      setAccountBusinesses(relatedBusinesses);
      setExpandedGroups((prev) => {
        const next = {};
        relatedBusinesses.forEach((entry) => {
          if (!entry?.id) return;
          next[entry.id] = Object.prototype.hasOwnProperty.call(prev, entry.id)
            ? prev[entry.id]
            : true;
        });
        return next;
      });
    } catch (err) {
      if (err?.status === 401 || err?.status === 403) {
        clearAuthToken();
        navigate('/login', { replace: true });
        return;
      }
      setError(err?.data?.error || err?.message || 'Failed to load business detail');
      setBusiness(null);
      setAccountBusinesses([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const selectedBusiness = useMemo(
    () => accountBusinesses.find((entry) => String(entry?.id) === String(selectedBusinessId)) || business,
    [accountBusinesses, business, selectedBusinessId]
  );

  useEffect(() => {
    const brandId = selectedBusiness?.id || selectedBusiness?.brandId;
    if (!brandId) {
      setClearinghouseDetail(null);
      return;
    }
    let cancelled = false;
    async function loadClearinghouseDetail() {
      setClearinghouseLoading(true);
      try {
        const data = await apiFetch(`/ops/clearinghouse/brand/${encodeURIComponent(brandId)}`);
        if (!cancelled) setClearinghouseDetail(data?.detail || data || null);
      } catch (_err) {
        if (!cancelled) setClearinghouseDetail(null);
      } finally {
        if (!cancelled) setClearinghouseLoading(false);
      }
    }
    loadClearinghouseDetail();
    return () => {
      cancelled = true;
    };
  }, [selectedBusiness?.id, selectedBusiness?.brandId]);

  const selectedStores = useMemo(
    () => (Array.isArray(selectedBusiness?.stores?.rows) ? selectedBusiness.stores.rows : []),
    [selectedBusiness]
  );
  const fundingEinOptions = useMemo(() => {
    const rows = accountBusinesses.length ? accountBusinesses : (business ? [business] : []);
    const byId = new Map();
    rows.forEach((entry) => {
      if (!entry?.id || byId.has(entry.id)) return;
      byId.set(entry.id, entry);
    });
    return Array.from(byId.values()).sort((a, b) => {
      const aHasOverride = hasActiveFundingOverride(getBusinessFundingOverride(a));
      const bHasOverride = hasActiveFundingOverride(getBusinessFundingOverride(b));
      if (aHasOverride !== bHasOverride) return aHasOverride ? -1 : 1;
      const aName = `${a?.name || 'Business'} ${a?.einTaxId || ''}`;
      const bName = `${b?.name || 'Business'} ${b?.einTaxId || ''}`;
      return aName.localeCompare(bName);
    });
  }, [accountBusinesses, business]);
  const fundingEinOverrideCount = fundingEinOptions.filter((entry) => hasActiveFundingOverride(getBusinessFundingOverride(entry))).length;
  const fundingStoreOptions = useMemo(
    () => [...selectedStores].sort((a, b) => {
      const aHasOverride = hasActiveFundingOverride(getStoreFundingOverride(a));
      const bHasOverride = hasActiveFundingOverride(getStoreFundingOverride(b));
      if (aHasOverride !== bHasOverride) return aHasOverride ? -1 : 1;
      const aName = formatStoreSelectorLabel(a);
      const bName = formatStoreSelectorLabel(b);
      return aName.localeCompare(bName);
    }),
    [selectedStores]
  );
  const fundingStoreOverrideCount = fundingStoreOptions.filter((store) => hasActiveFundingOverride(getStoreFundingOverride(store))).length;
  const selectedFundingStore = useMemo(
    () => selectedStores.find((store) => String(store?.storeId || store?.placeId || '') === String(selectedFundingStoreId || '')) || null,
    [selectedFundingStoreId, selectedStores]
  );
  const queryEinId = String(searchParams.get('einId') || '');
  const queryStoreId = String(searchParams.get('storeId') || '');
  const excludedStoreCount = selectedStores.filter((store) => store?.enrollmentStatus !== 'enrolled').length;
  const accountId = business?.accountWallet?.accountId || selectedBusiness?.accountWallet?.accountId || '';

  useEffect(() => {
    if (!accountId) return;
    setActions((prev) => ({
      ...prev,
      storeEnrollment: {
        ...prev.storeEnrollment,
        accountId: prev.storeEnrollment.accountId || accountId,
      },
    }));
  }, [accountId]);

  useEffect(() => {
    if (selectedBusiness?.onlineOnly || !selectedStores.length) {
      setSelectedFundingStoreId('');
      return;
    }
    const requestedStore = queryStoreId
      ? selectedStores.find((store) => String(store?.storeId || '') === queryStoreId || String(store?.placeId || '') === queryStoreId)
      : null;
    const currentStore = selectedStores.find((store) => String(store?.storeId || store?.placeId || '') === String(selectedFundingStoreId || ''));
    const nextStore = requestedStore || currentStore || selectedStores[0];
    setSelectedFundingStoreId(String(nextStore?.storeId || nextStore?.placeId || ''));
  }, [queryStoreId, selectedBusiness?.id, selectedBusiness?.onlineOnly, selectedFundingStoreId, selectedStores]);

  useEffect(() => {
    setFundingOverrideDrafts({
      account: overrideToDraft(selectedBusiness?.fundingPolicy?.overrides?.account),
      brand: overrideToDraft(selectedBusiness?.fundingPolicy?.overrides?.brand),
      store: overrideToDraft(selectedFundingStore?.fundingPolicyOverride),
    });
    setFundingOverrideErrors({ account: '', brand: '', store: '' });
  }, [
    selectedBusiness?.id,
    accountId,
    selectedFundingStore?.storeId,
    selectedFundingStore?.placeId,
    selectedFundingStore?.fundingPolicyOverride,
    selectedBusiness?.fundingPolicy?.overrides?.account,
    selectedBusiness?.fundingPolicy?.overrides?.brand,
  ]);

  function updateFundingOverrideDraft(scope, patch) {
    setFundingOverrideDrafts((prev) => ({
      ...prev,
      [scope]: {
        ...(prev[scope] || emptyFundingOverrideDraft()),
        ...patch,
      },
    }));
  }

  async function saveFundingOverride(scope) {
    const draft = fundingOverrideDrafts[scope] || emptyFundingOverrideDraft();
    const endpoint = scope === 'account'
      ? `/ops/accounts/${encodeURIComponent(accountId)}/funding-policy-override`
      : scope === 'store'
        ? `/ops/businesses/${encodeURIComponent(selectedBusiness?.id || '')}/stores/${encodeURIComponent(selectedFundingStore?.storeId || selectedFundingStore?.placeId || '')}/funding-policy-override`
        : `/ops/businesses/${encodeURIComponent(selectedBusiness?.id || '')}/funding-policy-override`;

    if (scope === 'account' && !accountId) {
      setFundingOverrideErrors((prev) => ({ ...prev, account: 'Account ID is required' }));
      return;
    }
    if (scope === 'brand' && !selectedBusiness?.id) {
      setFundingOverrideErrors((prev) => ({ ...prev, brand: 'Select a business first' }));
      return;
    }
    if (scope === 'store' && (!selectedBusiness?.id || !(selectedFundingStore?.storeId || selectedFundingStore?.placeId))) {
      setFundingOverrideErrors((prev) => ({ ...prev, store: 'Select a store first' }));
      return;
    }

    let payload;
    try {
      payload = draftToOverridePayload(draft);
    } catch (err) {
      setFundingOverrideErrors((prev) => ({ ...prev, [scope]: err?.message || 'Invalid override values' }));
      return;
    }

    setFundingOverrideSaving(scope);
    setFundingOverrideErrors((prev) => ({ ...prev, [scope]: '' }));
    try {
      await apiFetch(endpoint, { method: 'PUT', body: payload });
      setToastMessage('Funding policy override updated');
      await load();
    } catch (err) {
      setFundingOverrideErrors((prev) => ({
        ...prev,
        [scope]: err?.data?.error || err?.message || 'Failed to save funding policy override',
      }));
    } finally {
      setFundingOverrideSaving('');
    }
  }

  useEffect(() => {
    if (!queryEinId) return;
    if (String(selectedBusinessId || '') === queryEinId) return;
    const exists = accountBusinesses.some((entry) => String(entry?.id || '') === queryEinId);
    if (exists) setSelectedBusinessId(queryEinId);
  }, [accountBusinesses, queryEinId, selectedBusinessId]);

  const accountSummary = useMemo(() => {
    const source = accountBusinesses.length ? accountBusinesses : (business ? [business] : []);
    const accountBlockers = dedupeBlockers(
      source.flatMap((entry) => (Array.isArray(entry?.blockers?.account) ? entry.blockers.account : []))
    );
    const fundingExposure = business?.accountWallet?.fundingExposure || selectedBusiness?.accountWallet?.fundingExposure || {};
    const walletBalanceCents = Number(business?.accountWallet?.balanceCents || selectedBusiness?.accountWallet?.balanceCents || 0);
    const thresholdCents = Number(business?.accountWallet?.thresholdCents || selectedBusiness?.accountWallet?.thresholdCents || 0);

    return {
      accountLabel: accountId || 'Unknown Account',
      status: business?.summary?.accountStatus || selectedBusiness?.summary?.accountStatus || '—',
      walletBalanceCents,
      thresholdCents,
      thresholdSource: String(business?.accountWallet?.thresholdSource || selectedBusiness?.accountWallet?.thresholdSource || 'policy'),
      thresholdSourceLabel: business?.accountWallet?.thresholdSourceLabel || selectedBusiness?.accountWallet?.thresholdSourceLabel || null,
      walletState: business?.accountWallet?.state || selectedBusiness?.accountWallet?.state || '—',
      pendingConsumerSpendCents: Number(fundingExposure?.pendingConsumerSpendCents || 0),
      pendingExposureCents: Number(fundingExposure?.pendingExposureCents || 0),
      projectedAvailableBalanceCents: fundingExposure?.projectedAvailableBalanceCents == null
        ? walletBalanceCents
        : Number(fundingExposure.projectedAvailableBalanceCents || 0),
      exposureRatioPercent: fundingExposure?.exposureRatioPercent ?? null,
      fundingRiskState: fundingExposure?.riskState || 'normal',
      fundingRiskReasonCode: fundingExposure?.reasonCode || null,
      pendingTransactionCount: Number(fundingExposure?.pendingTransactionCount || 0),
      totalEins: source.length,
      totalStores: source.reduce((sum, entry) => sum + Number(entry?.stores?.totalCount || 0), 0),
      totalActiveStores: source.reduce((sum, entry) => sum + Number(entry?.stores?.activeCount || 0), 0),
      totalEnrolledStores: source.reduce((sum, entry) => sum + Number(entry?.stores?.enrolledCount || 0), 0),
      totalBrands: Number(business?.accountWallet?.totalBrands || selectedBusiness?.accountWallet?.totalBrands || source.length),
      totalContributions: source.reduce((sum, entry) => sum + Number(entry?.performance?.lifetimeContributionCents || 0), 0),
      totalOverallFees: source.reduce(
        (sum, entry) => sum + Number((entry?.performance?.lifetimeOverallFeeCents ?? entry?.performance?.lifetimeFeeCents) || 0),
        0
      ),
      totalFeeCredits: source.reduce((sum, entry) => sum + Number(entry?.performance?.lifetimeFeeCreditCents || 0), 0),
      totalCollectableFees: source.reduce(
        (sum, entry) => sum + Number(entry?.performance?.lifetimeCollectableFeeCents || 0),
        0
      ),
      last30dContributions: null,
      last30dFees: null,
      accountBlockers,
      accountActions: collectRecommendedActions(accountBlockers),
      accountStatusCauseCode: business?.summary?.causes?.accountStatusCauseCode || selectedBusiness?.summary?.causes?.accountStatusCauseCode || '',
      accountStatusCauseNote: business?.summary?.causes?.accountStatusCauseNote || selectedBusiness?.summary?.causes?.accountStatusCauseNote || '',
      visibleReadyEins: source.reduce((sum, entry) => sum + (entry?.summary?.consumerVisibleReady ? 1 : 0), 0),
      visibleReady: source.length > 0 && source.every((entry) => !!entry?.summary?.consumerVisibleReady),
      visibleState:
        source.length === 0
          ? 'none'
          : source.every((entry) => !!entry?.summary?.consumerVisibleReady)
            ? 'all'
            : source.some((entry) => !!entry?.summary?.consumerVisibleReady)
              ? 'partial'
              : 'none',
    };
  }, [accountBusinesses, accountId, business, selectedBusiness]);

  useEffect(() => {
    const requestedAction = String(searchParams.get('action') || '');
    if (!requestedAction || !selectedBusiness) return;
    if (queryEinId && String(selectedBusiness?.id || '') !== queryEinId) return;
    const consumeKey = [selectedBusiness?.id || '', requestedAction, queryEinId || '', queryStoreId || ''].join(':');
    if (consumedAutoAction === consumeKey) return;

    const exactStore = queryStoreId
      ? selectedStores.find((store) => String(store?.storeId || '') === queryStoreId || String(store?.placeId || '') === queryStoreId)
      : null;
    if (requestedAction === 'geography') {
      const firstUnresolvedStore = selectedStores.find((store) => {
        const resolution = store?.geographyResolution || {};
        const status = String(resolution?.status || (store?.marketKey ? 'resolved' : 'needs_ops_review')).toLowerCase();
        return status !== 'resolved';
      });
      const targetStore = exactStore || firstUnresolvedStore || selectedStores[0] || null;
      const targetStoreId = String(targetStore?.storeId || targetStore?.placeId || '');
      if (selectedBusiness?.id) {
        setExpandedGroups((prev) => ({ ...prev, [selectedBusiness.id]: true }));
      }
      setHighlightedGeographyStoreId(targetStoreId);
      setConsumedAutoAction(consumeKey);

      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('action');
      nextParams.delete('einId');
      nextParams.delete('storeId');
      setSearchParams(nextParams, { replace: true });

      window.setTimeout(() => {
        storeGeographySectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
      return;
    }

    if (!ACTION_CONFIG[requestedAction]) return;
    const firstExcludedStore = selectedStores.find((store) => store?.enrollmentStatus !== 'enrolled') || selectedStores[0] || null;
    const targetStore = exactStore || firstExcludedStore;
    let patch = null;

    if (requestedAction === 'accountStatus') {
      patch = {
        value: accountSummary.status || '',
        reasonCode: accountSummary.accountStatusCauseCode || '',
        reasonNote: accountSummary.accountStatusCauseNote || '',
      };
    } else if (requestedAction === 'brandStatus') {
      patch = {
        value: selectedBusiness?.summary?.status || '',
        reasonCode: selectedBusiness?.summary?.causes?.brandStatusCauseCode || '',
        reasonNote: selectedBusiness?.summary?.causes?.brandStatusCauseNote || '',
      };
    } else if (requestedAction === 'lifecycleStatus') {
      patch = {
        value: selectedBusiness?.summary?.lifecycleStatus || '',
        reasonCode: selectedBusiness?.summary?.causes?.lifecycleStatusCauseCode || '',
        reasonNote: selectedBusiness?.summary?.causes?.lifecycleStatusCauseNote || '',
      };
    } else if (requestedAction === 'hiddenFromConsumer') {
      patch = {
        value: selectedBusiness?.summary?.hiddenFromConsumer ? 'unhide' : 'hide',
        reasonCode: selectedBusiness?.summary?.causes?.hiddenOverrideCauseCode || '',
        reasonNote: selectedBusiness?.summary?.causes?.hiddenOverrideCauseNote || '',
      };
    } else if (requestedAction === 'storeEnrollment' && targetStore) {
      patch = {
        value: targetStore.enrollmentStatus === 'enrolled' ? 'paused' : 'active',
        storeId: targetStore.storeId || '',
        placeId: targetStore.placeId || '',
        accountId: targetStore.enrollmentAccountId || accountId || '',
        reasonCode: targetStore.exclusionReasonCode || '',
        reasonNote: targetStore.exclusionReasonNote || '',
        effectiveTo: targetStore.enrollmentEffectiveTo || '',
      };
    }

    if (!patch) return;

    setActions((prev) => ({
      ...prev,
      [requestedAction]: {
        ...(prev[requestedAction] || initialActionState()),
        ...patch,
        error: '',
        success: '',
      },
    }));
    setModalActionKey(requestedAction);
    setConsumedAutoAction(consumeKey);

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('action');
    nextParams.delete('einId');
    nextParams.delete('storeId');
    setSearchParams(nextParams, { replace: true });
  }, [
    accountId,
    accountSummary.accountStatusCauseCode,
    accountSummary.accountStatusCauseNote,
    accountSummary.status,
    consumedAutoAction,
    queryEinId,
    queryStoreId,
    searchParams,
    selectedBusiness,
    selectedStores,
    setSearchParams,
  ]);

  useEffect(() => {
    if (!toastMessage) return undefined;
    const timer = setTimeout(() => setToastMessage(''), 2500);
    return () => clearTimeout(timer);
  }, [toastMessage]);

  function applyOptimisticAction(actionKey, state, targetBusinessId) {
    function updateEntry(entry) {
      if (!entry) return entry;
      const entryId = String(entry?.id || '');
      const shouldUpdate = actionKey === 'accountStatus' || entryId === String(targetBusinessId || '');
      if (!shouldUpdate) return entry;

      let next = { ...entry, summary: { ...(entry.summary || {}) } };

      if (actionKey === 'accountStatus') {
        next.summary.accountStatus = state.value;
      }
      if (actionKey === 'brandStatus') {
        next.summary.status = state.value;
      }
      if (actionKey === 'lifecycleStatus') {
        next.summary.lifecycleStatus = state.value;
      }
      if (actionKey === 'hiddenFromConsumer') {
        next.summary.hiddenFromConsumer = state.value === 'hide';
      }
      if (actionKey === 'storeEnrollment' && next?.stores?.rows) {
        const rows = (Array.isArray(next.stores.rows) ? next.stores.rows : []).map((store) => {
          const matches = String(store?.storeId || '') === String(state.storeId || '');
          if (!matches) return store;
          const isPause = state.value === 'paused';
          return {
            ...store,
            status: isPause ? 'temp_excluded' : 'active',
            enrollmentStatus: isPause ? 'not_currently_enrolled' : 'enrolled',
            enrollmentEffectiveTo: isPause ? state.effectiveTo || store?.enrollmentEffectiveTo || null : null,
            exclusionReasonCode: isPause ? state.reasonCode || store?.exclusionReasonCode || null : null,
            exclusionReasonNote: isPause ? state.reasonNote || store?.exclusionReasonNote || null : null,
            visibilityStatus: isPause ? 'inactive' : (store?.isActive === false ? 'inactive' : 'active'),
          };
        });
        next.stores = {
          ...next.stores,
          rows,
          enrolledCount: rows.filter((store) => store?.enrollmentStatus === 'enrolled').length,
          activeCount: rows.filter((store) => store?.visibilityStatus === 'active').length,
        };
      }

      return next;
    }

    setBusiness((prev) => updateEntry(prev));
    setAccountBusinesses((prev) => prev.map((entry) => updateEntry(entry)));
  }

  function openActionModal(actionKey, patch = {}) {
    const current = actions[actionKey] || initialActionState();
    updateActionState(actionKey, {
      ...current,
      ...patch,
      error: '',
      success: '',
    });
    setModalActionKey(actionKey);
  }

  function closeModal() {
    setModalActionKey('');
  }

  function runRecommendedAction(action) {
    const actionKey = typeof action === 'string' ? action : action?.key;
    const firstExcludedStore = selectedStores.find((store) => store?.enrollmentStatus !== 'enrolled') || selectedStores[0] || null;
    if (actionKey === 'reactivate_account') {
      openActionModal('accountStatus', {
        value: 'active',
        reasonCode: accountSummary.accountStatusCauseCode || '',
        reasonNote: accountSummary.accountStatusCauseNote || '',
      });
      return;
    }
    if (actionKey === 'update_brand_status') {
      openActionModal('brandStatus', {
        value: selectedBusiness?.summary?.status || '',
        reasonCode: selectedBusiness?.summary?.causes?.brandStatusCauseCode || '',
        reasonNote: selectedBusiness?.summary?.causes?.brandStatusCauseNote || '',
      });
      return;
    }
    if (actionKey === 'update_lifecycle_status') {
      openActionModal('lifecycleStatus', {
        value: selectedBusiness?.summary?.lifecycleStatus || '',
        reasonCode: selectedBusiness?.summary?.causes?.lifecycleStatusCauseCode || '',
        reasonNote: selectedBusiness?.summary?.causes?.lifecycleStatusCauseNote || '',
      });
      return;
    }
    if (actionKey === 'update_consumer_visibility') {
      openActionModal('hiddenFromConsumer', {
        value: selectedBusiness?.summary?.hiddenFromConsumer ? 'unhide' : 'hide',
        reasonCode: selectedBusiness?.summary?.causes?.hiddenOverrideCauseCode || '',
        reasonNote: selectedBusiness?.summary?.causes?.hiddenOverrideCauseNote || '',
      });
      return;
    }
    if (['update_store_status', 'end_temp_exclusion', 'reinclude_store', 'adjust_exclusion_end_date'].includes(actionKey) && firstExcludedStore) {
      openActionModal('storeEnrollment', {
        value: firstExcludedStore.enrollmentStatus === 'enrolled' ? 'paused' : 'active',
        storeId: firstExcludedStore.storeId || '',
        placeId: firstExcludedStore.placeId || '',
        accountId: firstExcludedStore.enrollmentAccountId || accountId || '',
        reasonCode: firstExcludedStore.exclusionReasonCode || '',
        reasonNote: firstExcludedStore.exclusionReasonNote || '',
        effectiveTo: firstExcludedStore.enrollmentEffectiveTo || '',
      });
      return;
    }
    if (actionKey === 'contact_owner') {
      setShowContact(true);
      return;
    }
    if (actionKey === 'add_note') {
      openActionModal('addNote', {
        value: 'note',
        reasonNote: '',
      });
    }
  }

  function isActionShortcutEnabled(action) {
    const actionKey = typeof action === 'string' ? action : action?.key;
    if (action && typeof action === 'object' && action.opsCanExecute === false) return false;
    if (['reactivate_account', 'update_brand_status', 'update_lifecycle_status', 'update_consumer_visibility', 'contact_owner', 'add_note'].includes(actionKey)) {
      return true;
    }
    if (['update_store_status', 'end_temp_exclusion', 'reinclude_store', 'adjust_exclusion_end_date'].includes(actionKey)) {
      return selectedStores.length > 0;
    }
    return false;
  }

  async function runAction(actionKey) {
    const cfg = ACTION_CONFIG[actionKey];
    const state = actions[actionKey];
    const targetBusiness = selectedBusiness || business;
    if (!cfg || !targetBusiness) return;
    const endpoint = targetBusiness?.adminActions?.endpoints?.[cfg.endpointKey];
    if (!endpoint) return;
    if (actionKey !== 'addNote' && !state.value) {
      updateActionState(actionKey, { error: 'Select a value first', success: '' });
      return;
    }
    if (actionKey === 'addNote' && !String(state.reasonNote || '').trim()) {
      updateActionState(actionKey, { error: 'Add a note before saving', success: '' });
      return;
    }

    const routineLifecycleStatuses = new Set(['prospect', 'onboarding', 'pending_verification', 'active']);
    const requiresStatusCause = (actionKey === 'accountStatus' || actionKey === 'brandStatus') && state.value !== 'active';
    const requiresLifecycleCause = actionKey === 'lifecycleStatus' && !routineLifecycleStatuses.has(state.value);
    const requiresHideCause = actionKey === 'hiddenFromConsumer' && state.value === 'hide';
    const requiresStoreExclusionCause = actionKey === 'storeEnrollment' && state.value === 'paused';
    const requiresTipPolicyCause = actionKey === 'tipPolicy';

    if ((requiresStatusCause || requiresLifecycleCause || requiresHideCause || requiresStoreExclusionCause || requiresTipPolicyCause) && !state.reasonCode) {
      updateActionState(actionKey, { error: 'Select a reason code before saving', success: '' });
      return;
    }

    if ((state.reasonCode === 'other' || requiresTipPolicyCause) && !String(state.reasonNote || '').trim()) {
      updateActionState(actionKey, { error: requiresTipPolicyCause ? 'Add an audit note before saving' : 'Add an audit note when the reason code is other', success: '' });
      return;
    }

    if (actionKey === 'storeEnrollment' && state.value === 'paused' && !state.effectiveTo) {
      updateActionState(actionKey, { error: 'Set an end date for a temporary exclusion', success: '' });
      return;
    }

    updateActionState(actionKey, { loading: true, error: '', success: '' });
    try {
      const payload = {
        reasonCode: state.reasonCode || undefined,
        reasonNote: state.reasonNote || undefined,
        overrideTransition: !!state.overrideTransition,
      };

      if (actionKey === 'accountStatus' || actionKey === 'brandStatus' || actionKey === 'lifecycleStatus') {
        payload.status = state.value;
      } else if (actionKey === 'storeEnrollment') {
        if (!state.storeId) {
          updateActionState(actionKey, { loading: false, error: 'Select store first', success: '' });
          return;
        }
        if (!state.accountId) {
          updateActionState(actionKey, { loading: false, error: 'accountId is required', success: '' });
          return;
        }
        payload.status = state.value;
        payload.accountId = state.accountId;
        payload.effectiveFrom = state.effectiveFrom || new Date().toISOString();
        payload.effectiveTo = state.effectiveTo || undefined;
        payload.hiddenFromConsumer = !!state.enrollmentHiddenFromConsumer;
      } else if (actionKey === 'hiddenFromConsumer') {
        payload.hiddenFromConsumer = state.value === 'hide';
      } else if (actionKey === 'tipPolicy') {
        payload.restaurantSubtype = state.value === 'clear' ? null : state.value;
      } else if (actionKey === 'addNote') {
        payload.note = String(state.reasonNote || '').trim();
      }

      const finalEndpoint = actionKey === 'storeEnrollment'
        ? `${endpoint}/${encodeURIComponent(state.storeId)}`
        : endpoint;
      await apiFetch(finalEndpoint, { method: cfg.method || 'PUT', body: payload });
      applyOptimisticAction(actionKey, state, targetBusiness?.id || business?.id);
      setToastMessage('Updated');
      if (actionKey === 'addNote') setShowAudit(true);
      updateActionState(actionKey, { loading: false, error: '', success: 'Saved' });
      closeModal();
      await load();
    } catch (err) {
      updateActionState(actionKey, {
        loading: false,
        error: err?.data?.error || err?.message || 'Action failed',
        success: '',
      });
    }
  }

  async function runStoreGeographyAction(entry, store, action) {
    const storeId = String(store?.storeId || '').trim();
    const baseEndpoint = entry?.adminActions?.endpoints?.storeGeographyResolution;
    if (!entry?.adminActions?.canManageMarketOperations || !baseEndpoint || !storeId) return;

    let endpoint = `${baseEndpoint}/${encodeURIComponent(storeId)}/geography-resolution`;
    let method = 'PATCH';
    let body = {};

    if (action === 'retry') {
      endpoint += '/retry';
      method = 'POST';
      body = {};
    } else if (action === 'out_of_footprint') {
      const reason = window.prompt('Reason for marking this store intentionally out-of-footprint?');
      if (reason === null) return;
      endpoint += '/out-of-footprint';
      body = { reason: String(reason || '').trim() || 'Marked intentionally out-of-footprint by Ops' };
    } else if (action === 'resolve') {
      const marketKey = window.prompt('Canonical market key to resolve this store to?');
      if (!marketKey) return;
      const note = window.prompt('Resolution note?') || '';
      endpoint += '/resolve';
      body = { marketKey: String(marketKey).trim(), note: String(note).trim() || undefined };
    } else {
      return;
    }

    setToastMessage('');
    try {
      await apiFetch(endpoint, { method, body });
      setToastMessage('Geography updated');
      await load();
    } catch (err) {
      setToastMessage(err?.data?.error || err?.message || 'Failed to update geography');
    }
  }

  const modalConfig = ACTION_CONFIG[modalActionKey] || null;
  const modalState = modalActionKey ? actions[modalActionKey] : null;
  const fundingPolicy = selectedBusiness?.fundingPolicy || null;
  const fundingPerStore = fundingPolicy?.perStore || {};
  const fundingTotals = fundingPolicy?.totals || {};
  const fundingSources = fundingPolicy?.sources?.perStore || {};
  const fundingAggregateSources = fundingPolicy?.sources?.aggregate || {};
  const fundingOverrideIndicator = buildFundingOverrideIndicator(fundingSources, fundingAggregateSources);
  const fundingStorePolicies = Array.isArray(fundingPolicy?.storePolicies) ? fundingPolicy.storePolicies : [];
  const selectedFundingStorePolicy = fundingStorePolicies.find((policy) => (
    String(policy?.storeId || '') === String(selectedFundingStore?.storeId || '') ||
    String(policy?.placeId || '') === String(selectedFundingStore?.placeId || '')
  )) || selectedFundingStore?.fundingPolicy || null;
  const canManageFundingPolicy = selectedBusiness?.adminActions?.canManageBusinessStatuses === true;

  if (loading) {
    return (
      <OpsLayout title="Account Detail" subtitle="Loading business detail...">
        <div className="ops-nfp-content">Loading business detail…</div>
      </OpsLayout>
    );
  }

  if (!business) {
    return (
      <OpsLayout
        title="Account Detail"
        subtitle={error || 'Business not found'}
        headerActions={(
          <button type="button" className="ops-btn primary" onClick={() => navigate('/ops/businesses')}>
            Back
          </button>
        )}
      />
    );
  }

  const selectedTipDecision = selectedBusiness?.tipPolicy?.decision || '—';
  const selectedTipSubtype = selectedBusiness?.tipPolicy?.restaurantSubtype
    || selectedBusiness?.tipPolicy?.inferredRestaurantSubtype
    || '—';
  const selectedBrandingProfile = selectedBusiness?.brandingProfile || null;
  const selectedDbaNames = Array.isArray(selectedBrandingProfile?.associatedDbaNames)
    ? selectedBrandingProfile.associatedDbaNames.filter(Boolean)
    : [];
  const accountBlockerPreview = (accountSummary.accountBlockers || []).slice(0, 3);
  const selectedEinBlockers = Array.isArray(selectedBusiness?.blockers?.ein) ? selectedBusiness.blockers.ein : [];
  const selectedEinActions = collectRecommendedActions(selectedEinBlockers);
  const accountStatusCauseLabel = accountSummary.accountStatusCauseCode
    ? `${titleCaseWords(accountSummary.accountStatusCauseCode)}${accountSummary.accountStatusCauseNote ? `: ${accountSummary.accountStatusCauseNote}` : ''}`
    : 'None';
  const selectedBrandStatusCauseLabel = selectedBusiness?.summary?.causes?.brandStatusCauseCode
    ? `${titleCaseWords(selectedBusiness.summary.causes.brandStatusCauseCode)}${selectedBusiness?.summary?.causes?.brandStatusCauseNote ? `: ${selectedBusiness.summary.causes.brandStatusCauseNote}` : ''}`
    : 'None';
  const selectedLifecycleCauseLabel = selectedBusiness?.summary?.causes?.lifecycleStatusCauseCode
    ? `${titleCaseWords(selectedBusiness.summary.causes.lifecycleStatusCauseCode)}${selectedBusiness?.summary?.causes?.lifecycleStatusCauseNote ? `: ${selectedBusiness.summary.causes.lifecycleStatusCauseNote}` : ''}`
    : 'None';
  const selectedHiddenOverrideCauseLabel = selectedBusiness?.summary?.causes?.hiddenOverrideCauseCode
    ? `${titleCaseWords(selectedBusiness.summary.causes.hiddenOverrideCauseCode)}${selectedBusiness?.summary?.causes?.hiddenOverrideCauseNote ? `: ${selectedBusiness.summary.causes.hiddenOverrideCauseNote}` : ''}`
    : 'None';
  const accountStatusTone = accountSummary.status === 'active' ? 'good' : 'bad';
  const walletTone = accountSummary.walletState === 'funded'
    ? 'good'
    : (accountSummary.walletState === 'low' || accountSummary.walletState === 'depleted' ? 'bad' : '');
  const fundingRiskTone = accountSummary.fundingRiskState === 'projected_negative' || accountSummary.fundingRiskState === 'at_risk'
    ? 'bad'
    : (accountSummary.fundingRiskState === 'watch' ? 'warn' : 'good');
  const selectedStatusTone = selectedBusiness?.summary?.status === 'active' ? 'good' : 'bad';

  return (
    <OpsLayout
      title={`Account: ${accountSummary.accountLabel}`}
      subtitle={`${accountSummary.accountLabel} • ${business?.summary?.accountStatus || '—'} • ${formatMoney(accountSummary.walletBalanceCents)} • Threshold ${formatMoney(accountSummary.thresholdCents)} • ${accountSummary.walletState} • ${accountSummary.totalEins} EINs • ${accountSummary.totalStores} stores`}
      headerActions={(
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <span className="ops-chip">Lifetime Contributions: {formatMoney(accountSummary.totalContributions)}</span>
          <span className="ops-chip">Overall Fees: {formatMoney(accountSummary.totalOverallFees)}</span>
          <span className="ops-chip">Platform Fee Credits: {formatMoney(accountSummary.totalFeeCredits)}</span>
          <span className="ops-chip">Collectable Fees: {formatMoney(accountSummary.totalCollectableFees)}</span>
          <span className="ops-chip">Last 30d Contributions: —</span>
          <span className="ops-chip">Last 30d Fees: —</span>
          <button
            type="button"
            className="ops-btn"
            onClick={() => openActionModal('accountStatus', {
              value: 'suspended',
              reasonCode: accountSummary.accountStatusCauseCode || '',
              reasonNote: accountSummary.accountStatusCauseNote || '',
            })}
          >
            Suspend Account
          </button>
          <button type="button" className="ops-btn" disabled title="No wallet funding action is exposed from the current API.">
            Fund Wallet
          </button>
          <button type="button" className="ops-btn" onClick={() => openActionModal('addNote', { value: 'note', reasonNote: '' })}>
            Add Note
          </button>
          <button type="button" className="ops-btn primary" onClick={() => navigate('/ops/businesses')}>
            Back
          </button>
        </div>
      )}
    >
      <div className="ops-nfp-content">
        {toastMessage ? (
          <div className="ops-message" style={{ marginTop: 0, marginBottom: 12 }}>
            {toastMessage}
          </div>
        ) : null}
        <section
          className="ops-nfp-panel"
          style={{
            marginTop: 0,
            position: 'sticky',
            top: 10,
            zIndex: 5,
            background: 'rgba(255,255,255,0.96)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <HealthChip
                label="Account Status"
                value={accountSummary.status}
                tone={accountStatusTone}
                onEdit={() => openActionModal('accountStatus', {
                  value: accountSummary.status || '',
                  reasonCode: accountSummary.accountStatusCauseCode || '',
                  reasonNote: accountSummary.accountStatusCauseNote || '',
                })}
                title={`Change the account status. Current reason: ${accountStatusCauseLabel}. Funding still requires the account holder.`}
              />
              <HealthChip label="Wallet Funded" value={accountSummary.walletState} tone={walletTone} />
              <HealthChip label="Funding Mode" value={selectedBusiness?.summary?.fundingMode || '—'} />
              <HealthChip
                label="Primary Contact"
                value={selectedBusiness?.contactInformation?.name || 'Not set'}
                onEdit={() => setShowContact((prev) => !prev)}
                title="Show contact details."
              />
            </div>
            {showContact ? (
              <div style={{ display: 'grid', gap: 8, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
                <Field label="Contact Name" value={selectedBusiness?.contactInformation?.name || '—'} style={{ gridTemplateColumns: '120px 1fr' }} />
                <Field label="Role" value={selectedBusiness?.contactInformation?.role || '—'} style={{ gridTemplateColumns: '120px 1fr' }} />
                <Field label="Phone" value={selectedBusiness?.contactInformation?.phone || '—'} style={{ gridTemplateColumns: '120px 1fr' }} />
                <Field label="Email" value={selectedBusiness?.contactInformation?.email || '—'} style={{ gridTemplateColumns: '120px 1fr' }} />
              </div>
            ) : null}
          </div>
        </section>

        <section className="ops-nfp-columns-layout" style={{ marginTop: 18 }}>
          <div className="ops-nfp-column">
            <SummaryCard
              title="Account Wallet & Performance"
              actions={(
                <>
                  <HealthChip
                    label="Policy Override"
                    value={fundingOverrideIndicator.value}
                    tone={fundingOverrideIndicator.tone}
                    title={fundingOverrideIndicator.title}
                  />
                  <button type="button" className="ops-btn" title="Show funding diagnostics." onClick={() => setShowWalletDiagnostics((prev) => !prev)}>
                    {showWalletDiagnostics ? 'Hide Funding Diagnostics' : 'Show Funding Diagnostics'}
                  </button>
                  <button type="button" className="ops-btn primary" title="View and manage funding policy." onClick={() => setShowFundingPolicyModal(true)}>
                    Funding Policy
                  </button>
                </>
              )}
            >
              <Field label="Spendable wallet balance" value={formatMoney(accountSummary.walletBalanceCents)} />
              <Field label="Pending exposure" value={`${formatMoney(accountSummary.pendingExposureCents)} (${accountSummary.pendingTransactionCount} pending)`} />
              <Field label="Projected available balance" value={formatMoney(accountSummary.projectedAvailableBalanceCents)} />
              <Field
                label="Exposure ratio"
                value={accountSummary.exposureRatioPercent == null
                  ? titleCaseWords(accountSummary.fundingRiskState)
                  : `${formatPercent(accountSummary.exposureRatioPercent)} - ${titleCaseWords(accountSummary.fundingRiskState)}`}
              />
              <Field label="Minimum threshold" value={formatMoney(accountSummary.thresholdCents)} />
              <Field label="Threshold source" value={accountSummary.thresholdSourceLabel || titleCaseWords(accountSummary.thresholdSource)} />
              <Field label="Wallet state" value={accountSummary.walletState} />
              <HealthChip
                label="Funding Risk"
                value={titleCaseWords(accountSummary.fundingRiskState)}
                tone={fundingRiskTone}
                title={accountSummary.fundingRiskReasonCode ? titleCaseWords(accountSummary.fundingRiskReasonCode) : 'Pending exposure risk state'}
              />
              <Field
                label="Funding safety override"
                value={selectedBusiness?.fundingSafety?.active
                  ? `${titleCaseWords(selectedBusiness?.fundingSafety?.reasonCode || 'wallet_insolvent')}${selectedBusiness?.fundingSafety?.reasonNote ? `: ${selectedBusiness.fundingSafety.reasonNote}` : ''}`
                  : 'none'}
              />
              <Field label="Last top-up" value={formatDateTime(selectedBusiness?.walletFunding?.lastTopupAt)} />
              <Field label="Last top-up result" value={selectedBusiness?.walletFunding?.lastTopupResult || '—'} />
              <Field label="30d pre-fund failures" value={Number(business?.accountWallet?.preFundFailures30d || 0)} />
              <Field label="Brands in account" value={accountSummary.totalBrands} />
              <Field label="Stores in account" value={`${accountSummary.totalActiveStores} active / ${accountSummary.totalStores} total`} />
              {showWalletDiagnostics ? (
                <>
                  <Field label="Gross top-ups received" value={formatMoney(selectedBusiness?.walletFunding?.grossTopupsCents || 0)} />
                  <Field label="Processor fees deducted" value={formatMoney(selectedBusiness?.walletFunding?.processorFeesCents || 0)} />
                  <Field label="Fee credit remaining" value={formatMoney(selectedBusiness?.walletFunding?.feeCreditRemainingCents || 0)} />
                  <Field label="Fee credit used to date" value={formatMoney(selectedBusiness?.walletFunding?.feeCreditAppliedCents || 0)} />
                  <Field label="Funding basis" value={selectedBusiness?.walletFunding?.fundingBasis || 'cash only'} />
                </>
              ) : null}
            </SummaryCard>

            <SummaryCard
              title="Clearinghouse & Reactivation"
              actions={(
                <button
                  type="button"
                  className="ops-btn"
                  onClick={() => navigate(`/ops/businesses?clearinghouse=visible_period_unfunded_liability`)}
                >
                  Open clearinghouse queue
                </button>
              )}
            >
              {clearinghouseLoading ? (
                <div className="ops-message" style={{ marginTop: 0 }}>Loading clearinghouse detail…</div>
              ) : (
                <>
                  <Field
                    label="Hard liability"
                    value={`${Number(clearinghouseDetail?.clearinghouse?.visiblePeriodUnfundedLiability?.count || 0)} tx • ${formatMoney(clearinghouseDetail?.clearinghouse?.visiblePeriodUnfundedLiability?.amountCents || 0)}`}
                  />
                  <Field
                    label="Hidden candidates"
                    value={`${Number(clearinghouseDetail?.clearinghouse?.hiddenPeriodDeferredCandidates?.count || 0)} tx • ${formatMoney(clearinghouseDetail?.clearinghouse?.hiddenPeriodDeferredCandidates?.potentialAmountCents || 0)}`}
                  />
                  <Field
                    label="Applied contributions"
                    value={`${Number(clearinghouseDetail?.clearinghouse?.appliedContributions?.count || 0)} tx • ${formatMoney(clearinghouseDetail?.clearinghouse?.appliedContributions?.amountCents || 0)}`}
                  />
                  <Field
                    label="Refund offsets"
                    value={`${Number(clearinghouseDetail?.clearinghouse?.refundOffsets?.count || 0)} tx • ${formatMoney(clearinghouseDetail?.clearinghouse?.refundOffsets?.amountCents || 0)}`}
                  />
                  <Field label="Rejected on reprocess" value={Number(clearinghouseDetail?.clearinghouse?.rejectedOnReprocess?.count || 0)} />
                  <Field label="Expired deferred" value={Number(clearinghouseDetail?.clearinghouse?.expiredDeferredCandidates?.count || 0)} />
                  <Field
                    label="Latest gate decision"
                    value={clearinghouseDetail?.reactivationGate
                      ? `${titleCaseWords(clearinghouseDetail.reactivationGate.visibilityDecision)}${clearinghouseDetail.reactivationGate.blockedReason ? `: ${titleCaseWords(clearinghouseDetail.reactivationGate.blockedReason)}` : ''}`
                      : 'No reactivation gate audit'}
                  />
                  {clearinghouseDetail?.reactivationGate ? (
                    <>
                      <Field label="Post-settlement balance" value={formatMoney(clearinghouseDetail.reactivationGate.postSettlementWalletBalanceCents)} />
                      <Field label="Required visibility threshold" value={formatMoney(clearinghouseDetail.reactivationGate.requiredVisibilityThresholdCents)} />
                      <Field label="Gate evaluated" value={formatDateTime(clearinghouseDetail.reactivationGate.evaluatedAt)} />
                    </>
                  ) : null}
                  <HealthChip
                    label="Reconciliation"
                    value={clearinghouseDetail?.reconciliation?.mismatchCount ? `${clearinghouseDetail.reconciliation.mismatchCount} mismatch` : 'balanced'}
                    tone={clearinghouseDetail?.reconciliation?.mismatchCount ? 'bad' : 'good'}
                  />
                  {clearinghouseDetail?.warnings?.visibleWithUnsettledHardLiability ? (
                    <div className="ops-message error" style={{ marginTop: 10 }}>
                      {clearinghouseDetail.warnings.message}
                    </div>
                  ) : null}
                </>
              )}
            </SummaryCard>

          </div>

          <div className="ops-nfp-column">
            <SummaryCard
              title="Readiness Summary"
              footer={(
                <button type="button" className="ops-btn" title="Show visibility diagnostics." onClick={() => setShowVisibilityDiagnostics((prev) => !prev)}>
                  {showVisibilityDiagnostics ? 'Hide Diagnostics' : 'Show Diagnostics'}
                </button>
              )}
            >
              <Field
                label="Account Fully Visible"
                value={(
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <span>{accountSummary.visibleState === 'all' ? 'yes' : accountSummary.visibleState === 'partial' ? 'partial' : 'no'}</span>
                    <span
                      aria-hidden="true"
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        background:
                          accountSummary.visibleState === 'all'
                            ? '#16a34a'
                            : accountSummary.visibleState === 'partial'
                              ? '#ca8a04'
                              : '#dc2626',
                        display: 'inline-block',
                      }}
                    />
                  </span>
                )}
              />
              <Field
                label="Account blockers"
                value={accountBlockerPreview.length ? accountBlockerPreview.map(formatBlockerLabel).join(', ') : 'None'}
              />
              <div className="ops-kv">
                <div className="k">Recommended actions</div>
                <div className="v" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {(accountSummary.accountActions || []).length === 0 ? (
                    'None'
                  ) : (accountSummary.accountActions || []).map((action) => (
                    <button
                      key={action.key || action}
                      type="button"
                      className="ops-btn"
                      disabled={!isActionShortcutEnabled(action)}
                      title={isActionShortcutEnabled(action) ? `Run ${formatActionLabel(action)}.` : 'No direct shortcut is available for this action yet.'}
                      onClick={() => runRecommendedAction(action)}
                    >
                      {formatActionLabel(action)}
                    </button>
                  ))}
                </div>
              </div>
              <Field label="Account active store coverage" value={`${Number(accountSummary.totalActiveStores || 0)} active / ${Number(accountSummary.totalStores || 0)} total`} />
              <Field label="Discovery-ready EINs" value={`${Number(accountSummary.visibleReadyEins || 0)} / ${Number(accountSummary.totalEins || 0)}`} />
              {showVisibilityDiagnostics ? (
                <>
                  <Field label="All account blockers" value={(accountSummary.accountBlockers || []).map(formatBlockerLabel).join(', ') || 'None'} />
                  {accountSummary.status !== 'active' ? <Field label="Account status reason" value={accountStatusCauseLabel} /> : null}
                </>
              ) : null}
            </SummaryCard>
          </div>
        </section>

        <section className="ops-nfp-panel">
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'end' }}>
            <div className="ops-nfp-field" style={{ margin: 0, minWidth: 280, flex: '1 1 320px' }}>
              <label>Selected Business (EIN)</label>
              <select
                value={selectedBusiness?.id || ''}
                onChange={(e) => setSelectedBusinessId(e.target.value)}
              >
                {accountBusinesses.map((entry) => (
                  <option key={entry.id} value={entry.id}>
                    {`${entry.name || 'Business'}${entry.einTaxId ? ` (${entry.einTaxId})` : ''}`}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', flex: '2 1 420px' }}>
              <span className="ops-chip">EIN: {selectedBusiness?.einTaxId || '—'}</span>
              <span className="ops-chip">Stores: {Number(selectedBusiness?.stores?.totalCount || 0)}</span>
              <span className="ops-chip">
                Base Contribution: {Number.isFinite(Number(
                  selectedBusiness?.performance?.currentContributionRatePercent
                  ?? selectedBusiness?.baseContributionPercent
                ))
                  ? `${Number(
                    selectedBusiness?.performance?.currentContributionRatePercent
                    ?? selectedBusiness?.baseContributionPercent
                  )}%`
                  : '—'}
              </span>
              <span
                className="ops-chip"
                style={{
                  background: excludedStoreCount > 0 ? '#fee2e2' : '#dcfce7',
                  color: excludedStoreCount > 0 ? '#991b1b' : '#166534',
                  border: `1px solid ${excludedStoreCount > 0 ? '#fecaca' : '#bbf7d0'}`,
                }}
                title={excludedStoreCount > 0 ? 'Some stores are not currently enrolled.' : 'All stores are enrolled.'}
              >
                Stores Excluded: {excludedStoreCount}
              </span>
              <span className="ops-chip">Lifetime Contributions: {formatMoney(selectedBusiness?.performance?.lifetimeContributionCents || 0)}</span>
              <span className="ops-chip">
                Overall Fees: {formatMoney((selectedBusiness?.performance?.lifetimeOverallFeeCents ?? selectedBusiness?.performance?.lifetimeFeeCents) || 0)}
              </span>
              <span className="ops-chip">Platform Fee Credits: {formatMoney(selectedBusiness?.performance?.lifetimeFeeCreditCents || 0)}</span>
              <span className="ops-chip">Collectable Fees: {formatMoney(selectedBusiness?.performance?.lifetimeCollectableFeeCents || 0)}</span>
              <HealthChip
                label="Consumer Discovery"
                value={selectedBusiness?.summary?.consumerVisibleReady ? 'Visible' : 'Hidden'}
                tone={selectedBusiness?.summary?.consumerVisibleReady ? 'good' : 'bad'}
                onEdit={() => openActionModal('hiddenFromConsumer', {
                  value: selectedBusiness?.summary?.hiddenFromConsumer ? 'unhide' : 'hide',
                  reasonCode: selectedBusiness?.summary?.causes?.hiddenOverrideCauseCode || '',
                  reasonNote: selectedBusiness?.summary?.causes?.hiddenOverrideCauseNote || '',
                })}
                title={`Edit consumer discovery for the selected EIN. Current manual override reason: ${selectedHiddenOverrideCauseLabel}.`}
              />
              <HealthChip
                label="EIN Status"
                value={selectedBusiness?.summary?.status || '—'}
                tone={selectedStatusTone}
                onEdit={() => openActionModal('brandStatus', {
                  value: selectedBusiness?.summary?.status || '',
                  reasonCode: selectedBusiness?.summary?.causes?.brandStatusCauseCode || '',
                  reasonNote: selectedBusiness?.summary?.causes?.brandStatusCauseNote || '',
                })}
                title={`Change the selected EIN status. Current reason: ${selectedBrandStatusCauseLabel}.`}
              />
              <HealthChip
                label="Operational Stage"
                value={selectedBusiness?.summary?.lifecycleStatus || '—'}
                tone={selectedBusiness?.summary?.lifecycleStatus === 'active' ? 'good' : 'bad'}
                onEdit={() => openActionModal('lifecycleStatus', {
                  value: selectedBusiness?.summary?.lifecycleStatus || '',
                  reasonCode: selectedBusiness?.summary?.causes?.lifecycleStatusCauseCode || '',
                  reasonNote: selectedBusiness?.summary?.causes?.lifecycleStatusCauseNote || '',
                })}
                title={`Change the selected EIN operational stage. Current reason: ${selectedLifecycleCauseLabel}.`}
              />
              <button
                type="button"
                className="ops-btn"
                title="Open the registration-time DBA profile for this EIN."
                onClick={() => setShowBrandingModal(true)}
              >
                DBA Profile
              </button>
              <button
                type="button"
                className="ops-btn"
                title="Change tip classification."
                onClick={() => openActionModal('tipPolicy', {
                  value: selectedBusiness?.tipPolicy?.restaurantSubtype || '',
                })}
              >
                Tip Classification: {selectedTipDecision} / {selectedTipSubtype}
              </button>
            </div>
          </div>
          <div style={{ marginTop: 12, display: 'grid', gap: 8, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
            <Field label="EIN status reason" value={selectedBrandStatusCauseLabel} style={{ gridTemplateColumns: '140px 1fr' }} />
            <Field label="Operational stage reason" value={selectedLifecycleCauseLabel} style={{ gridTemplateColumns: '140px 1fr' }} />
            <Field label="Manual override reason" value={selectedHiddenOverrideCauseLabel} style={{ gridTemplateColumns: '140px 1fr' }} />
            <Field label="EIN status active" value={selectedBusiness?.visibilityChecks?.brandStatusActive ? 'yes' : 'no'} style={{ gridTemplateColumns: '140px 1fr' }} />
            <Field label="Operational stage active" value={selectedBusiness?.visibilityChecks?.lifecycleStatusActive ? 'yes' : 'no'} style={{ gridTemplateColumns: '140px 1fr' }} />
            <Field
              label="Operational stores"
              value={`${Number(selectedBusiness?.stores?.activeCount || 0)} / ${Number(selectedBusiness?.stores?.totalCount || 0)}`}
              style={{ gridTemplateColumns: '140px 1fr' }}
            />
            <Field
              label="Selected EIN blockers"
              value={selectedEinBlockers.map(formatBlockerLabel).join(', ') || 'None'}
              style={{ gridTemplateColumns: '140px 1fr', gridColumn: '1 / -1' }}
            />
            <div className="ops-kv" style={{ gridTemplateColumns: '140px 1fr', gridColumn: '1 / -1' }}>
              <div className="k">Selected EIN actions</div>
              <div className="v" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {selectedEinActions.length === 0 ? (
                  'None'
                ) : selectedEinActions.map((action) => (
                  <button
                    key={action.key || action}
                    type="button"
                    className="ops-btn"
                    disabled={!isActionShortcutEnabled(action)}
                    title={isActionShortcutEnabled(action) ? `Run ${formatActionLabel(action)}.` : 'No direct shortcut is available for this action yet.'}
                    onClick={() => runRecommendedAction(action)}
                  >
                    {formatActionLabel(action)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="ops-nfp-panel" ref={storeGeographySectionRef}>
          <div
            style={{
              marginBottom: 14,
              padding: '12px 14px',
              borderRadius: 14,
              background: '#fff7ed',
              color: '#9a3412',
              fontWeight: 600,
            }}
          >
            Rule: all stores must be included; only temporary exclusions are allowed and must carry an end date.
          </div>
          {(selectedBusiness ? [selectedBusiness] : []).map((entry) => {
            const groupStores = Array.isArray(entry?.stores?.rows) ? entry.stores.rows : [];
            const expanded = expandedGroups[entry.id] !== false;
            const alerts = [];
            if (!entry?.summary?.consumerVisibleReady) alerts.push('Hidden');
            if (entry?.tipPolicy?.unableToDecide) alerts.push('Tip review');
            return (
              <div key={entry.id} style={{ marginBottom: 14, border: '1px solid rgba(15, 23, 42, 0.08)', borderRadius: 16, overflow: 'hidden' }}>
                <button
                  type="button"
                  className="ops-btn"
                  onClick={() => setExpandedGroups((prev) => ({ ...prev, [entry.id]: !expanded }))}
                  aria-expanded={expanded}
                  style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 12,
                    border: 'none',
                    borderRadius: 0,
                    background: '#f8fafc',
                    padding: '14px 16px',
                    fontWeight: 700,
                  }}
                >
                  <span>{expanded ? '▾' : '▸'} {entry.name || 'Business'} • {entry.einTaxId || '—'} • Coverage {Number(entry?.stores?.enrolledCount || 0)}/{Number(entry?.stores?.totalCount || 0)}</span>
                  <span>{alerts.length ? `Alerts: ${alerts.join(', ')}` : 'Alerts: none'}</span>
                </button>
                {expanded ? (
                  <div style={{ padding: 14 }}>
                    <div className="ops-nfp-table-wrap">
                      <table className="ops-nfp-table">
                        <thead>
                          <tr>
                            <th>Store / Location</th>
                            <th>Coverage</th>
                            <th>Exclusion Reason / End Date</th>
                            <th>Geography</th>
                            <th>Store Active</th>
                            <th>Updated / Notes</th>
                            <th>Store Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {groupStores.length === 0 ? (
                            <tr>
                              <td colSpan={7}>No stores on this EIN.</td>
                            </tr>
                          ) : groupStores.map((store) => {
                            const isIncluded = store.enrollmentStatus === 'enrolled';
                            const coverageLabel = isIncluded
                              ? 'Enrolled'
                              : (store.enrollmentStatus === 'not_currently_enrolled'
                                ? 'Temporarily excluded'
                                : 'Not enrolled');
                            const hasMappingBlocker = (Array.isArray(store?.blockers) ? store.blockers : [])
                              .some((blocker) => blocker?.code === 'attribution_mapping_missing');
                            const geographyResolution = store?.geographyResolution || {};
                            const geographyStatus = geographyResolution?.status || (store?.marketKey ? 'resolved' : 'needs_ops_review');
                            const canManageGeography = entry?.adminActions?.canManageMarketOperations === true && !!store.storeId;
                            const isTargetedStore =
                              !!String(queryStoreId || highlightedGeographyStoreId || '') &&
                              (
                                String(store.storeId || '') === String(queryStoreId || highlightedGeographyStoreId || '') ||
                                String(store.placeId || '') === String(queryStoreId || highlightedGeographyStoreId || '')
                              );
                            return (
                              <tr
                                key={store.storeId || store.placeId || store.name}
                                style={isTargetedStore
                                  ? { background: '#eff6ff', boxShadow: 'inset 3px 0 0 #2563eb' }
                                  : undefined}
                              >
                                <td>
                                  <div>{store.name || 'Store'}</div>
                                  <div style={{ color: '#64748b', marginTop: 4 }}>{store.location || '—'}</div>
                                </td>
                                <td>
                                  <div>{coverageLabel}</div>
                                  {hasMappingBlocker ? (
                                    <div
                                      style={{
                                        marginTop: 4,
                                        display: 'inline-flex',
                                        padding: '2px 8px',
                                        borderRadius: 999,
                                        background: '#fee2e2',
                                        color: '#991b1b',
                                        fontSize: 12,
                                        fontWeight: 700,
                                      }}
                                      title="This store is currently included, but it is missing an explicit attribution mapping record."
                                    >
                                      Mapping missing
                                    </div>
                                  ) : null}
                                </td>
                                <td>
                                  <div>{store.notes || '—'}</div>
                                  <div style={{ color: '#64748b', marginTop: 4 }}>End: {formatDateTime(store.enrollmentEffectiveTo)}</div>
                                </td>
                                <td>
                                  <div>{store.marketKey || '—'}</div>
                                  <div style={{ color: '#64748b', marginTop: 4 }}>
                                    {titleCaseWords(geographyStatus)}
                                    {geographyResolution?.reasonCode ? ` • ${titleCaseWords(geographyResolution.reasonCode)}` : ''}
                                  </div>
                                  {canManageGeography ? (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                                      <button
                                        type="button"
                                        className="ops-btn"
                                        onClick={() => runStoreGeographyAction(entry, store, 'retry')}
                                        title="Retry Google/canonical geography resolution for this store."
                                      >
                                        Retry
                                      </button>
                                      <button
                                        type="button"
                                        className="ops-btn"
                                        onClick={() => runStoreGeographyAction(entry, store, 'resolve')}
                                        title="Manually resolve this store to a canonical market."
                                      >
                                        Resolve
                                      </button>
                                      <button
                                        type="button"
                                        className="ops-btn danger"
                                        onClick={() => runStoreGeographyAction(entry, store, 'out_of_footprint')}
                                        title="Mark this store intentionally out-of-footprint."
                                      >
                                        Out of Footprint
                                      </button>
                                    </div>
                                  ) : null}
                                </td>
                                <td>{store.visibilityStatus === 'active' ? 'yes' : 'no'}</td>
                                <td>
                                  <div>{formatDateTime(store.updatedAt)}</div>
                                  <div style={{ color: '#64748b', marginTop: 4 }}>{store.notes || '—'}</div>
                                </td>
                                <td>
                                  <button
                                    type="button"
                                    className="ops-btn"
                                    title={isIncluded ? 'Temporarily exclude this store. Reason and end date are required.' : 'Re-include this store.'}
                                    onClick={() => {
                                      setSelectedBusinessId(entry.id);
                                      openActionModal('storeEnrollment', {
                                        value: isIncluded ? 'paused' : 'active',
                                        storeId: store.storeId || '',
                                        placeId: store.placeId || '',
                                        accountId: store.enrollmentAccountId || accountId || '',
                                        effectiveTo: isIncluded ? '' : '',
                                      });
                                    }}
                                  >
                                    {isIncluded ? 'Exclude Temporarily' : 'Re-include'}
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </section>

        <section className="ops-nfp-panel">
          <button
            type="button"
            className="ops-btn"
            onClick={() => setShowAudit((prev) => !prev)}
            aria-expanded={showAudit}
            style={{ width: '100%', justifyContent: 'space-between', display: 'flex' }}
          >
            <span>Audit & Notes ({(selectedBusiness?.auditTimeline || []).length} entries)</span>
            <span>{showAudit ? 'Hide' : 'Show'}</span>
          </button>
          {showAudit ? (
            <div style={{ marginTop: 14 }}>
              {(selectedBusiness?.auditTimeline || []).length === 0 ? <div>No audit entries.</div> : null}
              {(selectedBusiness?.auditTimeline || []).map((entry, idx) => (
                <div className="ops-kv" key={`${entry.scope}-${idx}`}>
                  <div className="k">{entry.scope || 'event'}</div>
                  <div className="v">
                    <div>
                      {entry.fromStatus || (entry.fromHidden != null ? String(entry.fromHidden) : '—')}
                      {' -> '}
                      {entry.toStatus || (entry.toHidden != null ? String(entry.toHidden) : '—')}
                    </div>
                    <div>storeId: {entry.storeId || '—'}</div>
                    <div>placeId: {entry.placeId || '—'}</div>
                    <div>actor: {entry.updatedBy || '—'}</div>
                    <div>at: {formatDateTime(entry.updatedAt)}</div>
                    <div>reasonCode: {entry.reasonCode || '—'}</div>
                    <div>reasonNote: {entry.reasonNote || '—'}</div>
                    <div>override: {entry.overrideTransition ? 'yes' : 'no'}</div>
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </section>
      </div>

      <Modal
        open={showFundingPolicyModal}
        title="Funding Policy"
        onClose={() => setShowFundingPolicyModal(false)}
        width="min(760px, 100%)"
      >
        <div style={{ display: 'grid', gap: 14 }}>
          <ModalSection
            title="Policy Summary"
            description="Current funding values for this account and selected EIN."
          >
            <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
              <PolicyMetric label="Category" value={fundingPolicy?.category || selectedBusiness?.category || '—'} />
              <PolicyMetric label="Active stores" value={Number(fundingTotals?.activeStores || fundingPolicy?.storesActive || 0)} />
              <FundingPolicyMetric
                label="Initial funding"
                cents={fundingTotals?.initialTopUpCents || fundingTotals?.minTopUpCents || 0}
                source={fundingAggregateSources.initialTopUpCents || fundingAggregateSources.minTopUpCents}
              />
              <FundingPolicyMetric
                label="Next top-up"
                cents={fundingTotals?.reloadCents || 0}
                source={fundingAggregateSources.reloadCents}
              />
            </div>
          </ModalSection>

          <ModalSection
            title="Per-Store Policy"
            description="Base values multiplied by active store count unless a narrower override applies."
          >
            <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
              {FUNDING_POLICY_FIELDS.map((field) => (
                <FundingPolicyMetric
                  key={field.key}
                  label={field.label}
                  cents={fundingPerStore[field.key]}
                  source={fundingSources[field.key]}
                />
              ))}
            </div>
          </ModalSection>

          {!selectedBusiness?.onlineOnly && selectedStores.length ? (
            <ModalSection
              title="Store Location Override"
              description="Applies only to the selected physical location and takes precedence over all other funding policy sources."
              meta={`${fundingStoreOverrideCount} of ${fundingStoreOptions.length} stores have an override`}
            >
              <label className="ops-nfp-field" style={{ margin: 0 }}>
                <span>Store</span>
                <select
                  value={selectedFundingStoreId}
                  onChange={(e) => setSelectedFundingStoreId(e.target.value)}
                  disabled={fundingOverrideSaving === 'store'}
                >
                  {fundingStoreOptions.map((store) => {
                    const storeId = store.storeId || store.placeId || '';
                    const label = `${hasActiveFundingOverride(getStoreFundingOverride(store)) ? '[override] ' : ''}${formatStoreSelectorLabel(store)}`;
                    return (
                      <option key={storeId} value={storeId}>
                        {label}
                      </option>
                    );
                  })}
                </select>
              </label>
              <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
                <PolicyMetric label="Selected store" value={selectedFundingStore?.name || selectedFundingStore?.location || '—'} />
                <PolicyMetric label="Address" value={formatStoreAddress(selectedFundingStore) || '—'} valueFontSize={16} />
                <PolicyMetric label="City / State" value={formatStoreCityState(selectedFundingStore) || '—'} valueFontSize={16} />
                <PolicyMetric
                  label="Override"
                  value={hasActiveFundingOverride(getStoreFundingOverride(selectedFundingStore)) ? 'Enabled' : 'Not set'}
                />
              </div>
              {selectedFundingStorePolicy ? (
                <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
                  {FUNDING_POLICY_FIELDS.map((field) => (
                    <FundingPolicyMetric
                      key={field.key}
                      label={field.label}
                      cents={selectedFundingStorePolicy?.perStore?.[field.key]}
                      source={selectedFundingStorePolicy?.sources?.[field.key]}
                    />
                  ))}
                </div>
              ) : null}
              <FundingOverrideForm
                title="Override Values"
                description="Set only the values that should differ for this store."
                draft={fundingOverrideDrafts.store}
                onChange={(patch) => updateFundingOverrideDraft('store', patch)}
                onSave={() => saveFundingOverride('store')}
                saving={fundingOverrideSaving === 'store'}
                error={fundingOverrideErrors.store}
                disabled={!canManageFundingPolicy || !selectedFundingStore}
              />
            </ModalSection>
          ) : null}

          <ModalSection
            title="Account Override"
            description="Applies to every business under this account unless an EIN or store location has its own override."
          >
            <FundingOverrideForm
              title="Override Values"
              description="Set only the values that should differ for the account."
              draft={fundingOverrideDrafts.account}
              onChange={(patch) => updateFundingOverrideDraft('account', patch)}
              onSave={() => saveFundingOverride('account')}
              saving={fundingOverrideSaving === 'account'}
              error={fundingOverrideErrors.account}
              disabled={!canManageFundingPolicy || !accountId}
            />
          </ModalSection>

          <ModalSection
            title="EIN Override"
            description="Applies only to the selected business/EIN and takes precedence over the account override."
            meta={`${fundingEinOverrideCount} of ${fundingEinOptions.length} EINs have an override`}
          >
            <label className="ops-nfp-field" style={{ margin: 0 }}>
              <span>EIN</span>
              <select
                value={selectedBusiness?.id || ''}
                onChange={(e) => setSelectedBusinessId(e.target.value)}
                disabled={fundingOverrideSaving === 'brand'}
              >
                {fundingEinOptions.map((entry) => {
                  const hasOverride = hasActiveFundingOverride(getBusinessFundingOverride(entry));
                  const label = `${hasOverride ? '[override] ' : ''}${entry.name || 'Business'}${entry.einTaxId ? ` (${entry.einTaxId})` : ''}`;
                  return (
                    <option key={entry.id} value={entry.id}>
                      {label}
                    </option>
                  );
                })}
              </select>
            </label>
            <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
              <PolicyMetric label="Selected EIN" value={selectedBusiness?.einTaxId || '—'} />
              <PolicyMetric label="Business" value={selectedBusiness?.name || '—'} />
              <PolicyMetric label="Stores" value={Number(selectedBusiness?.stores?.totalCount || selectedStores.length || 0)} />
              <PolicyMetric
                label="Override"
                value={hasActiveFundingOverride(getBusinessFundingOverride(selectedBusiness)) ? 'Enabled' : 'Not set'}
              />
            </div>
            <FundingOverrideForm
              title="Override Values"
              description="Set only the values that should differ for this EIN."
              draft={fundingOverrideDrafts.brand}
              onChange={(patch) => updateFundingOverrideDraft('brand', patch)}
              onSave={() => saveFundingOverride('brand')}
              saving={fundingOverrideSaving === 'brand'}
              error={fundingOverrideErrors.brand}
              disabled={!canManageFundingPolicy || !selectedBusiness?.id}
            />
          </ModalSection>

          {!canManageFundingPolicy ? (
            <div style={{ color: '#64748b', fontSize: 13 }}>
              Your ops role can view policy sources but cannot edit funding policy overrides.
            </div>
          ) : null}
        </div>
      </Modal>

      <Modal open={!!modalConfig && !!modalState} title={modalConfig?.label || 'Update'} onClose={closeModal}>
        {modalConfig && modalState ? (
          <div className="ops-action-form-fields">
            {(modalConfig.options || []).length > 0 ? (
              <div className="ops-nfp-field">
                <label>Action</label>
                <select
                  title="Choose a new value."
                  value={modalState.value}
                  onChange={(e) => updateActionState(modalActionKey, { value: e.target.value, error: '', success: '' })}
                >
                  <option value="">Select</option>
                  {modalConfig.options.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
            ) : null}

            {modalActionKey === 'storeEnrollment' ? (
              <>
                <div className="ops-nfp-field">
                  <label>Store</label>
                  <select
                    title="Choose a store."
                    value={modalState.storeId}
                    onChange={(e) => {
                      const selected = selectedStores.find((store) => String(store.storeId) === String(e.target.value));
                      updateActionState(modalActionKey, {
                        storeId: e.target.value,
                        placeId: selected?.placeId || '',
                        accountId: selected?.enrollmentAccountId || accountId || '',
                      });
                    }}
                  >
                    <option value="">Select store</option>
                    {selectedStores.map((store) => (
                      <option key={store.storeId || store.placeId} value={store.storeId || ''}>
                        {`${store.name || 'Store'} (${store.enrollmentStatus || 'not_enrolled'})`}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="ops-nfp-field">
                  <label>Account ID</label>
                  <input
                    title="Review or update the account ID."
                    value={modalState.accountId}
                    onChange={(e) => updateActionState(modalActionKey, { accountId: e.target.value.trim() })}
                  />
                </div>
                <div className="ops-nfp-field">
                  <label>Effective From</label>
                  <input
                    type="datetime-local"
                    title="Set the start time."
                    value={modalState.effectiveFrom}
                    onChange={(e) => updateActionState(modalActionKey, { effectiveFrom: e.target.value })}
                  />
                </div>
                <div className="ops-nfp-field">
                  <label>End Date (temporary exclusions)</label>
                  <input
                    type="datetime-local"
                    title="Set the end time for a temporary exclusion."
                    value={modalState.effectiveTo}
                    onChange={(e) => updateActionState(modalActionKey, { effectiveTo: e.target.value })}
                  />
                </div>
                <label style={{ display: 'flex', gap: 8, alignItems: 'center' }} title="Also hide this exclusion from consumer discovery.">
                  <input
                    type="checkbox"
                    checked={!!modalState.enrollmentHiddenFromConsumer}
                    onChange={(e) => updateActionState(modalActionKey, { enrollmentHiddenFromConsumer: e.target.checked })}
                  />
                  Hidden from consumer while excluded
                </label>
              </>
            ) : null}

            {(modalConfig.reasonCodes || []).length > 0 ? (
              <div className="ops-nfp-field">
                <label>Reason Code</label>
                <select
                  title="Choose a reason code."
                  value={modalState.reasonCode}
                  onChange={(e) => updateActionState(modalActionKey, { reasonCode: e.target.value })}
                >
                  <option value="">Select reason code</option>
                  {(modalConfig.reasonCodes || []).map((code) => (
                    <option key={code} value={code}>{code}</option>
                  ))}
                </select>
              </div>
            ) : null}

            <div className="ops-nfp-field">
              <label>{modalActionKey === 'addNote' ? 'Note' : 'Audit Note'}</label>
              <textarea
                rows={4}
                title="Add an audit note."
                value={modalState.reasonNote}
                onChange={(e) => updateActionState(modalActionKey, { reasonNote: e.target.value })}
                placeholder={modalActionKey === 'addNote' ? 'Add the note you want saved to the audit trail.' : 'Required when the API action requires additional context.'}
              />
            </div>

            {modalActionKey !== 'addNote' ? (
              <label style={{ display: 'flex', gap: 8, alignItems: 'center' }} title="Use only to override normal transition rules.">
                <input
                  type="checkbox"
                  checked={!!modalState.overrideTransition}
                  onChange={(e) => updateActionState(modalActionKey, { overrideTransition: e.target.checked })}
                />
                Override transition
              </label>
            ) : null}

            {modalState.error ? <div className="ops-message error">{modalState.error}</div> : null}
            {modalState.success ? <div className="ops-message">{modalState.success}</div> : null}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button type="button" className="ops-btn" onClick={closeModal}>Cancel</button>
              <button
                type="button"
                className="ops-btn primary"
                disabled={modalState.loading}
                onClick={() => runAction(modalActionKey)}
              >
                {modalState.loading ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        ) : null}
      </Modal>

      <Modal open={showBrandingModal} title="DBA Profile" onClose={() => setShowBrandingModal(false)}>
        <div style={{ display: 'grid', gap: 14 }}>
          <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'minmax(180px, 220px) 1fr' }}>
            <div className="k" style={{ color: '#46666d', fontWeight: 700 }}>Primary DBA</div>
            <div>{selectedBrandingProfile?.primaryDbaName || selectedBusiness?.name || '—'}</div>
            <div className="k" style={{ color: '#46666d', fontWeight: 700 }}>Legal Business Name</div>
            <div>{selectedBrandingProfile?.legalName || '—'}</div>
            <div className="k" style={{ color: '#46666d', fontWeight: 700 }}>Associated DBAs</div>
            <div>
              {selectedDbaNames.length ? (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {selectedDbaNames.map((name) => (
                    <span key={name} className="ops-chip">{name}</span>
                  ))}
                </div>
              ) : '—'}
            </div>
          </div>

          <div
            style={{
              display: 'grid',
              gap: 16,
              gridTemplateColumns: selectedBrandingProfile?.logoUrl ? 'minmax(180px, 220px) 1fr' : '1fr',
              alignItems: 'start',
            }}
          >
            {selectedBrandingProfile?.logoUrl ? (
              <div
                style={{
                  border: '1px solid rgba(15, 23, 42, 0.08)',
                  borderRadius: 16,
                  padding: 14,
                  background: '#f8fafc',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: 180,
                }}
              >
                <img
                  src={selectedBrandingProfile.logoUrl}
                  alt={`${selectedBrandingProfile?.primaryDbaName || selectedBusiness?.name || 'DBA'} logo`}
                  style={{ maxWidth: '100%', maxHeight: 150, objectFit: 'contain' }}
                />
              </div>
            ) : null}

            <div style={{ display: 'grid', gap: 10 }}>
              <Field label="Tagline" value={selectedBrandingProfile?.tagline || '—'} style={{ gridTemplateColumns: '120px 1fr' }} />
              <div style={{ display: 'grid', gap: 6 }}>
                <div className="k" style={{ color: '#46666d', fontWeight: 700 }}>Description</div>
                <div
                  style={{
                    border: '1px solid rgba(15, 23, 42, 0.08)',
                    borderRadius: 14,
                    padding: '12px 14px',
                    background: '#ffffff',
                    color: '#113036',
                    whiteSpace: 'pre-wrap',
                    minHeight: 96,
                  }}
                >
                  {selectedBrandingProfile?.description || '—'}
                </div>
              </div>
            </div>
          </div>

          {!selectedBrandingProfile?.logoUrl && !selectedBrandingProfile?.tagline && !selectedBrandingProfile?.description ? (
            <div className="ops-message" style={{ marginTop: 0 }}>
              No branding assets or copy were captured for this EIN during registration.
            </div>
          ) : null}
        </div>
      </Modal>
    </OpsLayout>
  );
}
